import { PointerDragBehavior } from '@babylonjs/core/Behaviors/Meshes/pointerDragBehavior';
import { WebVRFreeCamera } from '@babylonjs/core/Cameras/VR/webVRCamera';
import { PointerEventTypes, PointerInfo } from '@babylonjs/core/Events/pointerEvents';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3, Quaternion, Vector3 } from '@babylonjs/core/Maths/math';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import { CylinderBuilder } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { SphereBuilder } from '@babylonjs/core/Meshes/Builders/sphereBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Observable, Observer } from '@babylonjs/core/Misc/observable';
import { UtilityLayerRenderer } from '@babylonjs/core/Rendering/utilityLayerRenderer';
import { Scene } from '@babylonjs/core/scene';
import { Nullable } from '@babylonjs/core/types';
import { rotationToVector, rotationToVectorToRef } from '@view/helpers/math-helper';

/**
 * Single plane rotation gizmo
 */
export class KbAxisMorphGizmo {
    /**
     * Drag behavior responsible for the gizmos dragging interactions
     */
    public dragBehavior: PointerDragBehavior;
    public dragActive = false;
    public direction = new Vector3();
    private _rootMesh: Mesh;
    private axisMesh: AbstractMesh;
    private _pointerObserver: Nullable<Observer<PointerInfo>> = null;
    private _tempVector = new Vector3();
    private _updateQuat = new Quaternion();

    protected _scaleRatio = 1;

    /**
     * Ratio for the scale of the gizmo (Default: 1)
     */
    public set scaleRatio(value: number) {
        this._scaleRatio = value;
    }

    public get scaleRatio() {
        return this._scaleRatio;
    }

    public set gizmoPosition(value: Vector3) {
        this._rootMesh.position = value;
    }

    public get gizmoPosition() {
        return this._rootMesh.position;
    }

    /**
     * Rotation distance in radians that the gizmo will snap to (Default: 0)
     */
    public snapDistance = 0;
    /**
     * Event that fires each time the gizmo snaps to a new location.
     * * snapDistance is the the change in distance
     */
    public updateObservable = new Observable<{ delta: Quaternion }>();

    private _beforeRenderObserver: Nullable<Observer<Scene>>;

    /**
     * Creates a PlaneRotationGizmo
     * @param gizmoLayer The utility layer the gizmo will be added to
     * @param color The color of the gizmo
     * @param tessellation Amount of tessellation to be used when creating rotation circles
     */
    constructor(
        color: Color3 = Color3.Gray(),
        private gizmoLayer: UtilityLayerRenderer = UtilityLayerRenderer.DefaultUtilityLayer,
        tessellation = 24
    ) {
        this._rootMesh = new Mesh('gizmoRootNode', gizmoLayer.utilityLayerScene);

        // Create Material
        const coloredMaterial = new StandardMaterial('', gizmoLayer.utilityLayerScene);
        coloredMaterial.emissiveColor = color;

        const wireframeMaterial = new StandardMaterial('', gizmoLayer.utilityLayerScene);
        wireframeMaterial.emissiveColor = color;
        wireframeMaterial.alpha = 0.4;
        // coloredMaterial.diffuseColor = color;
        // coloredMaterial.specularColor = color.subtract(new Color3(0.1, 0.1, 0.1));

        const hoverMaterial = new StandardMaterial('', gizmoLayer.utilityLayerScene);
        hoverMaterial.emissiveColor = color.add(new Color3(0.3, 0.3, 0.3));
        wireframeMaterial.alpha = 0.6;
        // hoverMaterial.diffuseColor = color.add(new Color3(0.3, 0.3, 0.3));

        // Build mesh to hold axis line
        this.axisMesh = new AbstractMesh('', gizmoLayer.utilityLayerScene);
        this.axisMesh.rotationQuaternion = Quaternion.Identity();

        const line = CylinderBuilder.CreateCylinder(
            '',
            { diameterTop: 0.01, height: 0.3, diameterBottom: 0.01, tessellation },
            gizmoLayer.utilityLayerScene
        );
        line.material = coloredMaterial;
        line.position.y = 0.375;
        const line2 = CylinderBuilder.CreateCylinder(
            '',
            { diameterTop: 0.005, height: 0.9, diameterBottom: 0.005, tessellation },
            gizmoLayer.utilityLayerScene
        );
        line2.material = coloredMaterial;
        line2.position.y = -0.225;

        const sphereMesh = SphereBuilder.CreateSphere(
            '',
            { segments: tessellation, diameter: 0.45 },
            gizmoLayer.utilityLayerScene
        );
        sphereMesh.material = wireframeMaterial;

        this.axisMesh.isPickable = false;
        this.axisMesh.addChild(line);
        this.axisMesh.addChild(line2);

        sphereMesh.addChild(this.axisMesh);
        sphereMesh.scaling.scaleInPlace(1 / 3);
        this._rootMesh.addChild(sphereMesh);

        let lastDragPosition: Vector3 | null = null;

        // Add drag behavior to handle events when the gizmo is dragged
        this.dragBehavior = new PointerDragBehavior();
        this.dragBehavior.moveAttached = false;

        this.dragBehavior.onDragStartObservable.add(() => {
            this.dragActive = true;
        });
        this.dragBehavior.onDragObservable.add(event => {
            // activeCamera.getForwardRayToRef(forwardRay, 1);
            // const cameraDirection = forwardRay.direction;
            // cameraDirection.rotateByQuaternionToRef(parentMesh.rotationQuaternion!, cameraDirection);
            // cameraDirection.addToRef(event.delta, targetDirection);
            // rotationToVectorToRef(targetDirection, cameraDirection, this._updateQuat);
            // parentMesh.rotationQuaternion!.multiplyInPlace(this._updateQuat);
            // this.updateObservable.notifyObservers({ delta: this._updateQuat });
        });
        this.dragBehavior.onDragEndObservable.add(() => {
            this.dragActive = false;
        });

        this._rootMesh.addBehavior(this.dragBehavior);
        this.dragBehavior.enabled = true;

        this._pointerObserver = gizmoLayer.utilityLayerScene.onPointerObservable.add(pointerInfo => {
            const isHovered =
                pointerInfo.pickInfo &&
                this._rootMesh.getChildMeshes().indexOf(<Mesh>pointerInfo.pickInfo.pickedMesh) != -1;
            sphereMesh.material = isHovered ? hoverMaterial : wireframeMaterial;

            if (pointerInfo.pickInfo && isHovered) {
                const point = pointerInfo.pickInfo.pickedPoint;
                if (point) {
                    point.subtractInPlace(this._rootMesh.position);
                    switch (pointerInfo.type) {
                        case PointerEventTypes.POINTERDOWN:
                            lastDragPosition = point.clone();
                            break;
                        case PointerEventTypes.POINTERMOVE:
                            if (lastDragPosition && this.dragActive) {
                                rotationToVectorToRef(point, lastDragPosition, this._updateQuat);
                                lastDragPosition.copyFrom(point);
                                // console.log(point, this._updateQuat);
                                this.direction.rotateByQuaternionToRef(this._updateQuat, this.direction);
                                this.updateObservable.notifyObservers({ delta: this._updateQuat });
                            }
                            break;
                    }
                }
            }
        });

        // let light = gizmoLayer._getSharedGizmoLight();
        // light.includedOnlyMeshes = light.includedOnlyMeshes.concat(this._rootMesh.getChildMeshes(false));

        this._beforeRenderObserver = this.gizmoLayer.utilityLayerScene.onBeforeRenderObservable.add(() => {
            this._update();
        });
    }
    protected _update() {
        if (this.direction) {
            this.axisMesh.rotationQuaternion = rotationToVector(this.direction);
        }
        const activeCamera = this.gizmoLayer.utilityLayerScene.activeCamera!;
        let cameraPosition = activeCamera.globalPosition;
        if ((<WebVRFreeCamera>activeCamera).devicePosition) {
            cameraPosition = (<WebVRFreeCamera>activeCamera).devicePosition;
        }
        this._rootMesh.position.subtractToRef(cameraPosition, this._tempVector);
        const dist = this._tempVector.length() * this.scaleRatio;
        this._rootMesh.scaling.set(dist, dist, dist);

        // Account for handedness, similar to Matrix.decompose
        if (this.gizmoLayer.originalScene.meshes[0]?._getWorldMatrixDeterminant() < 0) {
            this._rootMesh.scaling.y *= -1;
        }
    }

    /**
     * Disposes of the gizmo
     */
    public dispose() {
        this.gizmoLayer.utilityLayerScene.onPointerObservable.remove(this._pointerObserver);
        this._rootMesh.dispose();
        this.dragBehavior.detach();

        if (this._beforeRenderObserver) {
            this.gizmoLayer.utilityLayerScene.onBeforeRenderObservable.remove(this._beforeRenderObserver);
        }
    }
}
